import { Link, useRouterState } from "@tanstack/react-router";
import classNames from "classnames";
import { useContext } from "react";

import UserContext from "@/contexts/user";

import {
  getActiveNavSection,
  isNavPathActive,
  getVisibleNavItems,
} from "@/components/navConfig";

export default function SubNav() {
  const { user } = useContext(UserContext);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeSection = getActiveNavSection(pathname, user);

  if (!activeSection) {
    return null;
  }

  const visibleItems = getVisibleNavItems(activeSection, user);

  return (
    <aside className="flex h-full flex-col">
      <nav className="flex flex-col py-4">
        <p className="mb-2 px-5 text-xs font-semibold uppercase tracking-widest text-zinc-400">
          {activeSection.title}
        </p>

        {visibleItems.map((item, index) => {
          const IconComponent = item.icon;
          const isActive =
            item.to && !item.comingSoon
              ? isNavPathActive(pathname, item.to)
              : false;
          const previousItem = visibleItems[index - 1];
          const shouldRenderGroupLabel =
            item.group && item.group !== previousItem?.group;

          const groupLabel = shouldRenderGroupLabel ? (
            <p
              className={classNames(
                "px-5 pb-1 text-xs font-medium text-zinc-400",
                index === 0 ? "pt-1" : "pt-3"
              )}
            >
              {item.group}
            </p>
          ) : null;

          if (item.comingSoon || !item.to) {
            return (
              <div key={item.key}>
                {groupLabel}
                <div
                  className="flex cursor-not-allowed items-center gap-3 px-5 py-2.5 text-sm text-zinc-300"
                  aria-disabled="true"
                >
                  {IconComponent && (
                    <IconComponent size={15} className="shrink-0" />
                  )}
                  <span>{item.title}</span>
                  <span className="ml-auto rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-400">
                    Soon
                  </span>
                </div>
              </div>
            );
          }

          return (
            <div key={item.key}>
              {groupLabel}
              <Link
                to={item.to}
                className={classNames(
                  "flex min-h-10 items-center gap-3 px-5 py-2.5 text-sm transition-[background-color,color,transform] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-100 active:scale-[0.96]",
                  isActive
                    ? "border-r-2 border-blue-400 bg-blue-50 font-medium text-blue-500"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                )}
              >
                {IconComponent && (
                  <IconComponent
                    size={15}
                    className={classNames(
                      "shrink-0",
                      isActive ? "text-blue-400" : "text-zinc-400"
                    )}
                  />
                )}
                {item.title}
              </Link>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
